import "@/App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { ProfessionalProvider } from "./contexts/ProfessionalContext";
import { Toaster } from "./components/ui/sonner";
import { FEATURE_FLAGS } from "./featureFlags";

import { ThemeProvider } from "./contexts/ThemeContext";
import { LandingPage } from "./pages/LandingPage";
import { SolutionPage } from "./pages/SolutionPage";
import { ScrollToTop } from "./components/ScrollToTop";
import { SubscriptionGatePage } from "./pages/SubscriptionGatePage";

// Lazy loading pages
const Login = lazy(() => import("./pages/Login").then(m => ({ default: m.Login })));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/ResetPassword").then(m => ({ default: m.ResetPassword })));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage").then(m => ({ default: m.VerifyEmailPage })));
const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.DashboardPage })));
const AgendaPage = lazy(() => import("./pages/AgendaPage").then(m => ({ default: m.AgendaPage })));
const PatientsPage = lazy(() => import("./pages/PatientsPage").then(m => ({ default: m.PatientsPage })));
const PatientEvolutionPage = lazy(() => import("./pages/PatientEvolutionPage").then(m => ({ default: m.PatientEvolutionPage })));
const NewEvolutionPage = lazy(() => import("./pages/NewEvolutionPage").then(m => ({ default: m.NewEvolutionPage })));
const FinancialDashboard = lazy(() => import("./pages/FinancialDashboard").then(m => ({ default: m.FinancialDashboard })));
const ServicesPage = lazy(() => import("./pages/ServicesPage").then(m => ({ default: m.ServicesPage })));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage").then(m => ({ default: m.PaymentsPage })));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage").then(m => ({ default: m.ExpensesPage })));
const UsersPage = lazy(() => import("./pages/UsersPage").then(m => ({ default: m.UsersPage })));
const RepassesPage = lazy(() => import("./pages/RepassesPage").then(m => ({ default: m.RepassesPage })));
const InsurancesPage = lazy(() => import("./pages/InsurancesPage").then(m => ({ default: m.InsurancesPage })));
const TissBillingPage = lazy(() => import("./pages/TissBillingPage").then(m => ({ default: m.TissBillingPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then(m => ({ default: m.ReportsPage })));
const AuditPage = lazy(() => import("./pages/AuditPage").then(m => ({ default: m.AuditPage })));
const InsightsPage = lazy(() => import("./pages/InsightsPage").then(m => ({ default: m.InsightsPage })));
const PublicAppointmentPage = lazy(() => import("./pages/PublicAppointmentPage").then(m => ({ default: m.PublicAppointmentPage })));
const ClinicsAdminPage = lazy(() => import("./pages/ClinicsAdminPage").then(m => ({ default: m.ClinicsAdminPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then(m => ({ default: m.SignupPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));

// sistemaneurobit.com.br (raiz) é a landing page de marketing; o sistema em si
// vive em app.sistemaneurobit.com.br (e em qualquer outro host, ex. localhost,
// domínio *.up.railway.app de fallback) — mesma URL pra todas as clínicas,
// isoladas por tenant_id no backend.
// Em dev local (npm start), ?landing na URL simula o host de marketing sem
// precisar mexer em /etc/hosts — nunca funciona no build de produção.
const isLandingHost = typeof window !== 'undefined' && (
  window.location.hostname === 'sistemaneurobit.com.br'
  || (process.env.NODE_ENV !== 'production' && new URLSearchParams(window.location.search).has('landing'))
);

// Assinatura da clínica pendente/atrasada (ver AuthContext + security/auth.py no
// backend) bloqueia toda a área autenticada até o pagamento ser regularizado.
function AppRoutes() {
  const { subscriptionIssue } = useAuth();

  if (subscriptionIssue) {
    return <SubscriptionGatePage />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verificar-email" element={<VerifyEmailPage />} />
      {FEATURE_FLAGS.ENABLE_DASHBOARD && (
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.ENABLE_AGENDA && (
        <>
          <Route
            path="/agenda"
            element={
              <ProtectedRoute>
                <AgendaPage />
              </ProtectedRoute>
            }
          />

        </>
      )}
      {FEATURE_FLAGS.ENABLE_PATIENTS && (
        <>
          <Route
            path="/pacientes"
            element={
              <ProtectedRoute>
                <PatientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pacientes/:id/prontuario"
            element={
              <ProtectedRoute>
                <PatientEvolutionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pacientes/:id/evolucao/nova"
            element={
              <ProtectedRoute>
                <NewEvolutionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pacientes/:id/evolucao/editar/:evoId"
            element={
              <ProtectedRoute>
                <NewEvolutionPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {FEATURE_FLAGS.ENABLE_FINANCIAL && (
        <>
          <Route
            path="/financeiro"
            element={
              <ProtectedRoute>
                <FinancialDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/servicos"
            element={
              <ProtectedRoute>
                <ServicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/pagamentos"
            element={
              <ProtectedRoute>
                <PaymentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/repasses"
            element={
              <ProtectedRoute>
                <RepassesPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {FEATURE_FLAGS.ENABLE_EXPENSES && (
        <Route
          path="/despesas"
          element={
            <ProtectedRoute>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.ENABLE_USERS && (
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute>
              <UsersPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.ENABLE_INSURANCES && (
        <Route
          path="/convenios"
          element={
            <ProtectedRoute>
              <InsurancesPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.ENABLE_TISS && (
        <Route
          path="/faturamento/tiss"
          element={
            <ProtectedRoute>
              <TissBillingPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.ENABLE_REPORTS && (
        <Route
          path="/relatorios"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.ENABLE_AUDIT && (
        <Route
          path="/auditoria"
          element={
            <ProtectedRoute>
              <AuditPage />
            </ProtectedRoute>
          }
        />
      )}

      {FEATURE_FLAGS.ENABLE_INSIGHTS && (
        <Route
          path="/indicadores"
          element={
            <ProtectedRoute>
              <InsightsPage />
            </ProtectedRoute>
          }
        />
      )}
      <Route
        path="/perfil"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publico/agendamento/:id"
        element={<PublicAppointmentPage />}
      />
      <Route
        path="/plataforma/clinicas"
        element={
          <ProtectedRoute>
            <ClinicsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <Navigate to={FEATURE_FLAGS.ENABLE_DASHBOARD ? '/dashboard' : '/agenda'} replace />
        }
      />
    </Routes>
  );
}

function App() {
  if (isLandingHost) {
    return (
      <ThemeProvider defaultTheme="light" storageKey="neuropsi-theme">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/solucoes/:slug" element={<SolutionPage />} />
            <Route path="/cadastro" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SignupPage />
              </Suspense>
            } />
            <Route path="*" element={<LandingPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="neuropsi-theme">
      <AuthProvider>
        <ProfessionalProvider>
          <div className="App bg-background text-foreground min-h-screen">
          <BrowserRouter>
            <Suspense fallback={<LoadingSpinner />}>
              <AppRoutes />
            </Suspense>
          </BrowserRouter>
          <Toaster />
        </div>
        </ProfessionalProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
